import React, { useState, useEffect } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CButton,
  CFormInput,
  CProgress,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from '@coreui/react';
import axios from "axios";

const ImportProducts = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [history, setHistory] = useState([]);

  const tokenStr = localStorage.getItem('user');
  
  // Custom headers function to allow overriding
  const getHeaders = (customHeaders = {}) => {
    return {
      headers: {
        "Authorization": `Bearer ${tokenStr}`,
        ...customHeaders
      }
    };
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUploadAndImport = async () => {
    if (!file) {
      setMessage({ type: 'danger', text: 'Будь ласка, оберіть файл (XML або Excel).' });
      return;
    }

    setUploading(true);
    setMessage({ type: 'info', text: 'Завантаження файлу...' });
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      // 1. Upload File
      const uploadRes = await axios.post(
        process.env.REACT_APP_SERVER_URL + '/api/admin/import/upload', 
        formData, 
        getHeaders({ "Content-Type": "multipart/form-data" })
      );

      const filename = uploadRes.data.filename;
      
      // 2. Start Import Command
      setMessage({ type: 'info', text: 'Файл завантажено. Запуск імпорту...' });
      
      await axios.post(
        process.env.REACT_APP_SERVER_URL + '/api/admin/import/run', 
        { filename: filename }, 
        getHeaders()
      );

      setUploading(false);
      setImporting(true);
      setMessage({ type: 'success', text: 'Імпорт успішно запущено у фоновому режимі.' });
      
    } catch (error) {
      setUploading(false);
      setImporting(false);
      console.error(error);
      const errorMsg = error.response?.data?.error || error.message || 'Помилка при завантаженні/імпорті.';
      setMessage({ type: 'danger', text: errorMsg });
    }
  };

  useEffect(() => {
    let interval = null;

    if (importing) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(
            process.env.REACT_APP_SERVER_URL + '/api/admin/import/progress', 
            getHeaders()
          );
          
          if (res.data && res.data.status !== 'not_found') {
            setProgressData(res.data);
            
            if (res.data.status === 'completed') {
              setImporting(false);
              clearInterval(interval);
              setMessage({ type: 'success', text: 'Імпорт успішно завершено!' });
              fetchHistory();
            }
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }, 2000); // poll every 2 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [importing]);

  useEffect(() => {
    const fetchInitialProgress = async () => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_SERVER_URL + '/api/admin/import/progress', 
          getHeaders()
        );
        
        if (res.data && res.data.status !== 'not_found') {
          setProgressData(res.data);
          
          if (res.data.status !== 'completed' && res.data.status !== 'error') {
            setImporting(true);
          }
        }
      } catch (error) {
        console.error('Error fetching initial progress:', error);
      }
    };

    const fetchHistory = async () => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_SERVER_URL + '/api/admin/import/history',
          getHeaders()
        );
        if (res.data) {
          setHistory(res.data);
        }
      } catch (error) {
        console.error('Error fetching history:', error);
      }
    };

    fetchInitialProgress();
    fetchHistory();
  }, []);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Display in local time string (e.g., 24.03.2026, 14:00:00)
    return date.toLocaleString('UK-ua');
  };

  const renderProgressBar = () => {
    if (!progressData || !importing) {
        if (progressData && progressData.status === 'completed') {
             return (
                 <div className="mt-4">
                    <h5>Прогрес імпорту: {progressData.filename && <span className="fs-6 text-secondary">{progressData.filename}</span>}</h5>
                    <CProgress value={100} color="success" className="mb-2" />
                    <p className="mb-1">Оброблено: {progressData.total} з {progressData.total}</p>
                    <p className="text-success fw-bold mb-1">Створено: {progressData.created || 0} | Оновлено: {progressData.updated || 0}</p>
                    <p className="text-secondary small">
                      Початок: {formatTime(progressData.started_at)} | Завершення: {formatTime(progressData.updated_at)}
                    </p>
                 </div>
             )
        }
        return null;
    }

    const { total, processed, status, started_at, filename } = progressData;
    let percentage = 0;
    
    if (total > 0) {
      percentage = Math.round((processed / total) * 100);
    } else if (status === 'starting') {
      percentage = 0;
    } else {
      // Indeterminate for XML when total isn't immediately known
      percentage = 100;
      return (
        <div className="mt-4">
          <h5>Прогрес імпорту: {filename && <span className="fs-6 text-secondary">{filename}</span>}</h5>
          <CProgress animated value={100} color="info" className="mb-2" />
          <p className="mb-1">Оброблено: {processed}</p>
          <p className="text-secondary small">Початок: {formatTime(started_at)} | в процесі...</p>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <h5>Прогрес імпорту: {filename && <span className="fs-6 text-secondary">{filename}</span>}</h5>
        <CProgress value={percentage} color="info" className="mb-2">{percentage}%</CProgress>
        <p className="mb-1">Оброблено {processed} з {total}</p>
        <p className="text-secondary small">Початок: {formatTime(started_at)} | в процесі...</p>
      </div>
    );
  };

  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CCardHeader>
            <strong>Імпорт Товарів</strong> <small>(XML або Excel)</small>
          </CCardHeader>
          <CCardBody>
            {message.text && (
              <CAlert color={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
                {message.text}
              </CAlert>
            )}
            
            <div className="mb-3">
              <CFormInput 
                type="file" 
                id="importFile" 
                label="Виберіть файл для імпорту товарів" 
                onChange={handleFileChange}
                disabled={uploading || importing}
                accept=".xml,.xls,.xlsx,.csv"
              />
            </div>
            
            <CButton 
              color="primary" 
              onClick={handleUploadAndImport} 
              disabled={!file || uploading || importing}
            >
              {uploading ? 'Завантаження...' : importing ? 'Імпортування...' : 'Завантажити та Імпортувати'}
            </CButton>

            {renderProgressBar()}

          </CCardBody>
        </CCard>
      </CCol>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CCardHeader>
            <strong>Історія завантажень</strong>
          </CCardHeader>
          <CCardBody>
             {history.length > 0 ? (
                <CTable hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell scope="col">#</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Назва файлу</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Розмір</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Дата завантаження</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {history.map((item, index) => (
                      <CTableRow key={index}>
                        <CTableHeaderCell scope="row">{index + 1}</CTableHeaderCell>
                        <CTableDataCell>{item.filename}</CTableDataCell>
                        <CTableDataCell>{item.size}</CTableDataCell>
                        <CTableDataCell>{formatTime(item.uploaded_at)}</CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
             ) : (
                <p>Історія порожня.</p>
             )}
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default ImportProducts;
